import {
  ArrowRight,
  Check,
  ChevronDown,
  Download,
  FastForward,
  Film,
  Gauge,
  Headphones,
  Info,
  Library,
  ListMusic,
  Loader2,
  Lock,
  Mail,
  Maximize2,
  Mic2,
  Music2,
  Pause,
  Play,
  Rewind,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  Video,
  Volume2,
  VolumeX,
  Wand2,
  Zap
} from "lucide-react";
import { type CSSProperties, type Dispatch, FormEvent, type KeyboardEvent, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { showcaseReleases, type ShowcaseRelease, type ShowcaseSlug } from "./showcase";

type User = { id: string; email: string; name: string | null };
type MeResponse = { user: User; credits: { plan: string; remaining_credits: number } };
type NullableMeResponse = MeResponse | { user: null; credits: null };
type Route = "home" | "pricing" | "create" | "result" | "studio" | "mv" | "library" | "brand" | "contact";
type MediaFitMode = "contain" | "cover";
type MediaPlayerSettings = {
  volume: number;
  muted: boolean;
  playbackRate: number;
  fitMode: MediaFitMode;
};

const libraryExamples = [
  ["TAP TAP", "AI Pop Showcase", "2:48"],
  ["Neon Heartbeat", "Synth Pop", "2:12"],
  ["Brand Spark", "Commercial Hook", "0:30"],
  ["Game Loop 88", "Interactive Loop", "1:00"],
  ["Soft Rain", "Acoustic Ballad", "3:18"],
  ["Tokyo Pulse", "Electro Pop", "2:34"],
  ["Ocean Loop", "Podcast Intro", "0:45"],
  ["Future Anthem", "Release Pack", "1:45"]
];

const genres = ["Synth Pop", "Lo-fi R&B", "Cinematic", "Electro Pop", "Acoustic", "EDM", "Hip-Hop", "Jingle"];
const moods = ["Uplifting", "Dreamy", "Melancholy", "Energetic", "Cinematic", "Romantic", "Dark", "Hopeful"];
const languages = ["English", "Mandarin", "Cantonese", "Japanese", "Korean", "Spanish", "Instrumental"];
const voices = ["Female · Bright", "Female · Warm", "Male · Soft", "Male · Husky", "Choir", "Duet"];
const defaultMediaPlayerSettings: MediaPlayerSettings = {
  volume: 0.86,
  muted: false,
  playbackRate: 1,
  fitMode: "contain"
};
const heroShowcaseLimit = 4;
const showcaseFilterOptions = [
  { id: "all", label: "All" },
  { id: "cinematic", label: "Cinematic" },
  { id: "brand", label: "Brand" },
  { id: "artist", label: "Artist" }
] as const;
type ShowcaseFilterId = (typeof showcaseFilterOptions)[number]["id"];

const heroStudioStages = [
  {
    status: "Brief locked",
    prompt: "VÉLA by Zolo - TAP TAP. Bright AI pop, mobile rhythm, clean hook, release-ready chorus.",
    lyric: "[Chorus] Tap tap, feel the glow. Every move is ready to go.",
    coverTitle: "TAP TAP",
    packTitle: "Release pack queued",
    packMeta: "Waiting for song, MV, thumbnail, captions",
    playerTitle: "TAP TAP",
    playerMeta: "VÉLA by Zolo · job ready",
    progress: 18,
    seed: 2
  },
  {
    status: "Lyrics syncing",
    prompt: "Mapping chorus timing, lyric captions, section markers, and hook energy.",
    lyric: "[Hook] Tap tap, lights up. One more move and the room goes bright.",
    coverTitle: "LYRICS",
    packTitle: "Lyrics timeline",
    packMeta: "Captions and sections are being aligned",
    playerTitle: "Lyrics timeline",
    playerMeta: "Sync pass · 38%",
    progress: 42,
    seed: 5
  },
  {
    status: "MV building",
    prompt: "Rendering widescreen MV moments, thumbnail candidates, and branded title frames.",
    lyric: "[Scene] Neon cards, phone glow, performance cut, product rhythm.",
    coverTitle: "MV CUT",
    packTitle: "MV preview",
    packMeta: "Widescreen, vertical cut, player poster",
    playerTitle: "MV preview",
    playerMeta: "Video assembly · 71%",
    progress: 72,
    seed: 8
  },
  {
    status: "Release ready",
    prompt: "Song, MV, thumbnail, caption set, public credits, and launch pack are ready.",
    lyric: "Final: VÉLA by Zolo - TAP TAP. Written, Composed & Produced by Pak.",
    coverTitle: "READY",
    packTitle: "Open release pack",
    packMeta: "Credits, thumbnails, captions, certificate",
    playerTitle: "TAP TAP",
    playerMeta: "VÉLA by Zolo · release pack ready",
    progress: 100,
    seed: 11
  }
] as const;

function routeFromPath(pathname: string): Route {
  if (pathname.startsWith("/contact")) return "contact";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/app/library")) return "library";
  if (pathname.startsWith("/app/brand")) return "brand";
  if (pathname.startsWith("/app/result")) return "result";
  if (pathname.startsWith("/app/studio")) return "studio";
  if (pathname.startsWith("/app/mv")) return "mv";
  if (pathname.startsWith("/app/create")) return "create";
  return "home";
}

function navigate(path: string) {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers || {})
    }
  });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const errorPayload = payload as { error?: string };
    throw new Error(typeof payload === "string" ? payload : errorPayload.error || "Request failed");
  }
  return payload as T;
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => routeFromPath(window.location.pathname));
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    api<NullableMeResponse>("/api/auth/me")
      .then((payload) => setMe(payload.user ? payload : null))
      .catch(() => setMe(null));
  }, []);

  return (
    <div className="app-shell">
      {route === "home" && <LandingPage me={me} />}
      {route === "pricing" && <PricingPage me={me} />}
      {route === "create" && <CreatePage me={me} setMe={setMe} />}
      {route === "result" && <ResultPage me={me} />}
      {route === "studio" && <StudioPage me={me} />}
      {route === "mv" && <StudioPage me={me} />}
      {route === "library" && <LibraryPage me={me} />}
      {route === "brand" && <BrandKitPage me={me} />}
      {route === "contact" && <ContactPage me={me} />}
    </div>
  );
}

function Logo() {
  return (
    <button className="logo" onClick={() => navigate("/")}>
      <span className="logo-mark">
        <img src="/brand/zolo-music-logo.png?v=20260627-zmark" alt="" />
      </span>
      <span>
        Zolo <em>Music</em>
      </span>
    </button>
  );
}

function Nav({ me, app }: { me: MeResponse | null; app?: boolean }) {
  return (
    <header className={app ? "nav nav-app" : "nav"}>
      <Logo />
      {!app && (
        <nav className="nav-links">
          <a onClick={() => navigate("/")}>Workstation</a>
          <a href="#showcase">Recent works</a>
          <a onClick={() => navigate("/pricing")}>Pricing</a>
          <a onClick={() => navigate("/contact")}>Contact</a>
          <a href="#trust">Trust</a>
        </nav>
      )}
      <div className="nav-actions">
        {app && <CreditBadge credits={me?.credits.remaining_credits ?? 0} />}
        {!app && <button className="ghost" onClick={() => navigate("/app/create")}>Sign in</button>}
        <button className="primary small" onClick={() => navigate(app ? "/pricing" : "/app/studio")}>
          {app ? "Upgrade" : "Open Studio"} <ArrowRight size={15} />
        </button>
        {app && me && (
          <button className="avatar" title={me?.user.email || "Guest"}>
            {(me?.user.name || me?.user.email || "ZM").slice(0, 2).toUpperCase()}
          </button>
        )}
      </div>
    </header>
  );
}

function CreditBadge({ credits }: { credits: number }) {
  return (
    <span className="credit-badge">
      <Zap size={14} /> {credits} <em>credits</em>
    </span>
  );
}

function LandingPage({ me }: { me: MeResponse | null }) {
  const [selectedShowcaseSlug, setSelectedShowcaseSlug] = useState<ShowcaseSlug>(showcaseReleases[0].slug);

  function openShowcaseRelease(slug: ShowcaseSlug) {
    setSelectedShowcaseSlug(slug);
    window.setTimeout(() => {
      document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  return (
    <main>
      <Nav me={me} />
      <section className="hero">
        <div className="hero-orbit" aria-hidden="true">
          <div className="orbit-card orbit-left">
            <img className="orbit-thumb" src={posterUrl(showcaseReleases[0])} alt="" />
            <strong>{showcaseReleases[0].title}</strong>
            <span>{showcaseReleases[0].style}</span>
          </div>
          <div className="orbit-card orbit-right">
            <img className="orbit-thumb" src={posterUrl(showcaseReleases[3])} alt="" />
            <strong>{showcaseReleases[3].title}</strong>
            <span>{showcaseReleases[3].style}</span>
          </div>
        </div>
        <div className="hero-copy">
          <span className="eyebrow"><Sparkles size={15} /> AI Music + MV Workstation</span>
          <h1>Make songs and MVs</h1>
          <p>
            Zolo Music turns one brief into original music, cinematic music videos, thumbnails, credits, and launch assets for creators, brands, and indie artists.
          </p>
          <HeroPromptComposer />
          <div className="hero-stats">
            <span>Song generation</span>
            <span>MV generation</span>
            <span>Release pack workflow</span>
          </div>
          <HeroMvThumbs selectedSlug={selectedShowcaseSlug} onSelect={openShowcaseRelease} />
        </div>
        <HeroProductMockup />
      </section>

      <ShowcaseSection selectedSlug={selectedShowcaseSlug} onSelect={setSelectedShowcaseSlug} />

      <section className="section">
        <SectionHeader
          eyebrow="Workflow"
          title="From song idea to music video launch."
          sub="The unicorn wedge is not another song prompt box. Zolo sells a finished creative workflow: song, edit, MV, thumbnail, caption, license, and campaign pack."
        />
        <div className="workflow-grid">
          {[
            ["Prompt & lyrics", "Write the concept, hook, language, structure, and public credits.", Wand2],
            ["Generate song", "Route vocal song, instrumental, remix, extend, or section jobs.", Music2],
            ["Build the MV", "Create widescreen, vertical, lyric, and cinematic video packages.", Video],
            ["Launch pack", "Export thumbnails, captions, metadata, social cuts, and license certificates.", Download]
          ].map(([title, body, Icon], index) => (
            <div className="workflow-card" key={title as string}>
              <span className="step-number">0{index + 1}</span>
              <Icon size={22} />
              <h3>{title as string}</h3>
              <p>{body as string}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <SectionHeader eyebrow="Workstation" title="Built around the jobs that make music usable." sub="The product surface is designed for repeatable production, not one-off prompt gambling." />
        <div className="feature-grid">
          {[
            ["Media job router", "Queue song, instrumental, remix, extend, stems, and MV jobs.", Sparkles],
            ["Lyrics timeline", "Keep lyrics, sections, captions, and edits synchronized.", Mic2],
            ["Version control", "Compare takes, keep alternates, and promote the best asset.", ListMusic],
            ["MV builder", "Build vertical, square, widescreen, lyric, and cinematic video exports.", Film],
            ["Release library", "Store projects, stems, certificates, covers, and release packs.", Library],
            ["Rights guardrails", "Block imitation, cloning, and risky copyrighted inputs.", ShieldCheck]
          ].map(([title, body, Icon]) => (
            <div className="feature-card" key={title as string}>
              <Icon size={22} />
              <h3>{title as string}</h3>
              <p>{body as string}</p>
            </div>
          ))}
        </div>
      </section>

      <TrustSection />
      <CtaBand />
      <Footer />
    </main>
  );
}

function HeroPromptComposer() {
  return (
    <div className="prompt-composer">
      <button className="icon-button" onClick={() => navigate("/app/create")} aria-label="New project">
        <span>+</span>
      </button>
      <div className="composer-copy">
        <span>Chat to make music</span>
        <strong>VÉLA by Zolo debut single with a clean global pop hook</strong>
      </div>
      <button className="secondary composer-advanced" onClick={() => navigate("/app/studio")}>
        <Settings2 size={16} /> Studio
      </button>
      <button className="primary composer-create" onClick={() => navigate("/app/create")}>
        <Wand2 size={16} /> Create
      </button>
    </div>
  );
}

function HeroMvThumbs({ selectedSlug, onSelect }: { selectedSlug: ShowcaseSlug; onSelect: (slug: ShowcaseSlug) => void }) {
  const featuredReleases = showcaseReleases.slice(0, heroShowcaseLimit);

  return (
    <div className="hero-mv-strip" aria-label="Recent Zolo Music MV thumbnails">
      <div className="hero-mv-label">
        <span>Latest Zolo MVs</span>
        <strong>{showcaseReleases.length} release library</strong>
        <button
          className="hero-mv-view-all"
          type="button"
          onClick={() => document.getElementById("showcase")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        >
          View all <ArrowRight size={13} />
        </button>
      </div>
      <div className="hero-mv-grid">
        {featuredReleases.map((release) => (
          <button
            className={`hero-mv-card ${release.slug === selectedSlug ? "active" : ""}`}
            key={release.slug}
            onClick={() => onSelect(release.slug)}
            aria-label={`Open ${release.artist} - ${release.title} in showcase player`}
            aria-pressed={release.slug === selectedSlug}
          >
            <img src={posterUrl(release)} alt={`${release.artist} - ${release.title} MV thumbnail`} loading="lazy" />
            <span>
              <strong>{release.title}</strong>
              <em>{release.duration}</em>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HeroProductMockup() {
  const [jobStage, setJobStage] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timersRef = useRef<number[]>([]);
  const activeJob = heroStudioStages[jobStage];

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  function runDemoJob() {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
    setIsRunning(true);
    setJobStage(0);
    heroStudioStages.forEach((_, index) => {
      if (index === 0) return;
      timersRef.current.push(window.setTimeout(() => setJobStage(index), index * 760));
    });
    timersRef.current.push(window.setTimeout(() => setIsRunning(false), heroStudioStages.length * 760));
  }

  return (
    <div className="hero-mockup" aria-label="Zolo Music product preview">
      <div className="mockup-top">
        <span className={`pill job-status ${isRunning ? "running" : ""}`} aria-live="polite">
          {isRunning ? <Loader2 className="spin" size={13} /> : <Sparkles size={13} />} {activeJob.status}
        </span>
        <button className="primary mini" onClick={runDemoJob}>
          {isRunning ? "Running" : "Run job"}
        </button>
      </div>
      <div className="prompt-box">{activeJob.prompt}</div>
      <div className="job-progress" aria-label="Studio job progress">
        {heroStudioStages.map((stage, index) => (
          <button
            key={stage.status}
            className={index === jobStage ? "active" : ""}
            onClick={() => {
              timersRef.current.forEach((timer) => window.clearTimeout(timer));
              timersRef.current = [];
              setIsRunning(false);
              setJobStage(index);
            }}
            aria-label={`Preview ${stage.status}`}
          >
            <span style={{ "--progress": `${stage.progress}%` } as CSSProperties} />
            {stage.status}
          </button>
        ))}
      </div>
      <div className="mockup-grid">
        <div className="mock-card wide">
          <span className="muted">Lyrics timeline</span>
          <p>{activeJob.lyric}</p>
          <Waveform seed={activeJob.seed} active={isRunning || jobStage > 0} />
        </div>
        <div className="cover-art cover-a">
          <span>{activeJob.coverTitle}</span>
        </div>
        <div className="mock-card">
          <span className="muted">Release pack</span>
          <button className="video-frame" onClick={() => navigate("/app/brand")} aria-label="Open release pack">
            <Play size={24} />
            <strong>{activeJob.packTitle}</strong>
            <em>{activeJob.packMeta}</em>
          </button>
        </div>
      </div>
      <MiniPlayer title={activeJob.playerTitle} meta={activeJob.playerMeta} seed={activeJob.seed + 2} autoActive={isRunning || jobStage > 0} />
    </div>
  );
}

function ShowcaseSection({ selectedSlug, onSelect }: { selectedSlug: ShowcaseSlug; onSelect: (slug: ShowcaseSlug) => void }) {
  const [cinemaMode, setCinemaMode] = useState(false);
  const [autoPlayToken, setAutoPlayToken] = useState(0);
  const [playerSettings, setPlayerSettings] = useState<MediaPlayerSettings>(defaultMediaPlayerSettings);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<ShowcaseFilterId>("all");
  const selectedRelease = showcaseReleases.find((release) => release.slug === selectedSlug) || showcaseReleases[0];
  const selected: ShowcaseRelease = selectedRelease;
  const selectedIndex = Math.max(0, showcaseReleases.findIndex((release) => release.slug === selected.slug));
  const nextRelease = showcaseReleases[(selectedIndex + 1) % showcaseReleases.length];
  const filteredReleases = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return showcaseReleases
      .map((release, index) => ({ release, index }))
      .filter(({ release }) => matchesShowcaseFilter(release, activeFilter))
      .filter(({ release }) => !query || getShowcaseSearchText(release).includes(query));
  }, [activeFilter, searchQuery]);

  function playNextRelease() {
    setAutoPlayToken((token) => token + 1);
    onSelect(nextRelease.slug);
  }

  return (
    <section className="section showcase-section" id="showcase">
      <SectionHeader
        eyebrow="Zolo Music company releases"
        title="Finished MVs, not demo placeholders."
        sub="Our latest VÉLA releases show the product promise: generate music and generate the music video package in the same creative operating system."
      />
      <div className={`showcase-layout ${cinemaMode ? "cinema-mode" : ""}`}>
        <div className="showcase-player-panel">
          <ShowcaseVideoPlayer
            key={selected.slug}
            release={selected}
            nextRelease={nextRelease}
            settings={playerSettings}
            onSettingsChange={setPlayerSettings}
            autoPlayToken={autoPlayToken}
            cinemaMode={cinemaMode}
            onCinemaChange={setCinemaMode}
            onPlayNext={playNextRelease}
          />
          <div className="showcase-meta">
            <span className="eyebrow">{selected.useCase}</span>
            <h3>{selected.artist} - {selected.title}</h3>
            <p>{selected.tagline}</p>
            <div className="showcase-proof">
              {selected.proof.map((item) => <span key={item}>{item}</span>)}
            </div>
            {selected.assetLinks && selected.assetLinks.length > 0 && (
              <div className="showcase-assets" aria-label={`${selected.title} downloadable assets`}>
                {selected.assetLinks.map((asset) => (
                  <a key={asset.href} href={asset.href} download>
                    <Download size={15} /> {asset.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="showcase-library-panel" aria-label="Zolo Music release browser">
          <div className="showcase-library-head">
            <div>
              <span>Release library</span>
              <strong>{filteredReleases.length} of {showcaseReleases.length} MVs</strong>
            </div>
            <button
              className="library-reset"
              type="button"
              onClick={() => {
                setSearchQuery("");
                setActiveFilter("all");
              }}
            >
              Reset
            </button>
          </div>
          <label className="showcase-search">
            <Search size={16} />
            <input
              aria-label="Search releases"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search title, mood, use case..."
            />
          </label>
          <div className="showcase-filter-bar" aria-label="Release filters">
            {showcaseFilterOptions.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={activeFilter === filter.id ? "active" : ""}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="showcase-list" aria-label="Zolo Music release library">
            {filteredReleases.length > 0 ? (
              filteredReleases.map(({ release, index }) => (
                <button
                  className={`showcase-row ${release.slug === selected.slug ? "active" : ""}`}
                  key={release.slug}
                  onClick={() => onSelect(release.slug)}
                >
                  <img src={posterUrl(release)} alt={`${release.artist} ${release.title} MV thumbnail`} loading="lazy" />
                  <span className="showcase-index">{String(index + 1).padStart(2, "0")}</span>
                  <span className="showcase-row-copy">
                    <strong>{release.title}</strong>
                    <em>{release.style} · {release.duration}</em>
                    <small>{release.useCase}</small>
                  </span>
                </button>
              ))
            ) : (
              <div className="showcase-empty">
                <strong>No release matched.</strong>
                <span>Try another title, mood, or use case.</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="business-strip">
        <div>
          <strong>Song + MV is the paid wedge.</strong>
          <span>Creators pay for releases, brands pay for campaigns, studios pay for repeatable workflow and API volume.</span>
        </div>
        <button className="primary" onClick={() => navigate("/contact?dept=brand&project=song_mv&work=walk-away-in-red")}>
          Build a campaign <ArrowRight size={17} />
        </button>
      </div>
    </section>
  );
}

function getShowcaseSearchText(release: ShowcaseRelease) {
  return [
    release.artist,
    release.title,
    release.duration,
    release.style,
    release.theme,
    release.tagline,
    release.useCase,
    ...release.proof
  ].join(" ").toLowerCase();
}

function matchesShowcaseFilter(release: ShowcaseRelease, filter: ShowcaseFilterId) {
  if (filter === "all") return true;
  const text = getShowcaseSearchText(release);

  if (filter === "cinematic") return /cinematic|runway|fashion|rain|scene|story|soul-pop/.test(text);
  if (filter === "brand") return /brand|campaign|commercial|jingle|release pack|social|launch/.test(text);
  return /artist|single|ballad|jazz|comeback|genre|hook|idol|v[ée]la/.test(text);
}

function ShowcaseVideoPlayer({
  release,
  nextRelease,
  settings,
  onSettingsChange,
  autoPlayToken,
  cinemaMode,
  onCinemaChange,
  onPlayNext
}: {
  release: ShowcaseRelease;
  nextRelease: ShowcaseRelease;
  settings: MediaPlayerSettings;
  onSettingsChange: Dispatch<SetStateAction<MediaPlayerSettings>>;
  autoPlayToken: number;
  cinemaMode: boolean;
  onCinemaChange: Dispatch<SetStateAction<boolean>>;
  onPlayNext: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [duration, setDuration] = useState(() => parseDuration(release.duration));
  const [currentTime, setCurrentTime] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [controlWakeToken, setControlWakeToken] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlHideTimerRef = useRef<number | null>(null);
  const hiddenPointerDownRef = useRef(false);
  const { volume, muted, playbackRate, fitMode } = settings;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = muted;
  }, [muted, volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (autoPlayToken <= 0) return;
    const video = videoRef.current;
    if (!video) return;
    setEnded(false);
    setCurrentTime(0);
    void video.play().catch(() => undefined);
  }, [autoPlayToken, release.slug]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    if (controlHideTimerRef.current !== null) {
      window.clearTimeout(controlHideTimerRef.current);
      controlHideTimerRef.current = null;
    }
    if (playing && controlsVisible && !settingsOpen && !infoOpen && !ended) {
      controlHideTimerRef.current = window.setTimeout(() => {
        setControlsVisible(false);
        controlHideTimerRef.current = null;
      }, 2000);
    }
    return () => {
      if (controlHideTimerRef.current !== null) {
        window.clearTimeout(controlHideTimerRef.current);
        controlHideTimerRef.current = null;
      }
    };
  }, [playing, controlsVisible, settingsOpen, infoOpen, ended, controlWakeToken]);

  function revealControls() {
    setControlsVisible(true);
    setControlWakeToken((token) => token + 1);
  }

  async function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    revealControls();
    if (ended) {
      video.currentTime = 0;
      setEnded(false);
      setCurrentTime(0);
    }
    if (video.paused) {
      await video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }

  function handleVideoClick() {
    if (hiddenPointerDownRef.current) {
      hiddenPointerDownRef.current = false;
      revealControls();
      return;
    }
    void togglePlay();
  }

  function skip(seconds: number) {
    const video = videoRef.current;
    if (!video) return;
    revealControls();
    const next = Math.min(Math.max(video.currentTime + seconds, 0), video.duration || Number.POSITIVE_INFINITY);
    video.currentTime = Number.isFinite(next) ? next : Math.max(video.currentTime + seconds, 0);
    setCurrentTime(video.currentTime);
    setEnded(false);
  }

  async function openFullscreen() {
    const shell = shellRef.current;
    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!shell) return;
    revealControls();
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
      setIsFullscreen(false);
      return;
    }
    if (shell.requestFullscreen) {
      await shell.requestFullscreen().catch(() => undefined);
      setIsFullscreen(Boolean(document.fullscreenElement));
      return;
    }
    video?.webkitEnterFullscreen?.();
  }

  async function handleCinemaModeButton() {
    revealControls();
    const fullscreenActive = Boolean(document.fullscreenElement) || isFullscreen;
    if (fullscreenActive) {
      await document.exitFullscreen().catch(() => undefined);
      setIsFullscreen(false);
      onCinemaChange(true);
      return;
    }
    onCinemaChange((current) => !current);
  }

  function seek(value: number) {
    const video = videoRef.current;
    if (!video) return;
    revealControls();
    const next = Math.min(Math.max(value, 0), duration || 0);
    video.currentTime = next;
    setCurrentTime(next);
    setEnded(false);
  }

  function changeVolume(value: number) {
    revealControls();
    const next = Math.min(Math.max(value, 0), 1);
    onSettingsChange((current) => ({ ...current, muted: next === 0, volume: next }));
  }

  function handlePlayerKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const key = event.key.toLowerCase();
    if ([" ", "k", "j", "l", "m", "f", "t", "i"].includes(key)) event.preventDefault();
    if ([" ", "k", "j", "l", "m", "f", "t", "i"].includes(key)) revealControls();
    if (key === " " || key === "k") void togglePlay();
    if (key === "j") skip(-10);
    if (key === "l") skip(10);
    if (key === "m") onSettingsChange((current) => ({ ...current, muted: !current.muted }));
    if (key === "f") void openFullscreen();
    if (key === "t") void handleCinemaModeButton();
    if (key === "i") setInfoOpen((current) => !current);
  }

  const volumePercent = muted ? 0 : Math.round(volume * 100);
  const progressPercent = duration ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const remainingSeconds = duration ? Math.max(0, Math.ceil(duration - currentTime)) : 0;
  const showNextUp = duration > 0 && (ended || (currentTime > 4 && remainingSeconds <= 18));
  const cinemaButtonLabel = isFullscreen ? "Return to cinema mode" : cinemaMode ? "Exit cinema mode" : "Open cinema mode";

  return (
    <div
      className={`showcase-video-shell ${playing ? "is-playing" : ""} ${controlsVisible ? "controls-visible" : ""} ${settingsOpen || infoOpen ? "has-panel-open" : ""} media-fit-${fitMode}`}
      ref={shellRef}
      tabIndex={0}
      onKeyDown={handlePlayerKeyDown}
      onFocus={revealControls}
      onPointerDown={() => {
        hiddenPointerDownRef.current = playing && !controlsVisible && !settingsOpen && !infoOpen && !ended;
        revealControls();
      }}
      onPointerMove={() => revealControls()}
    >
      <video
        key={release.slug}
        ref={videoRef}
        className="showcase-video"
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onClick={handleVideoClick}
        onContextMenu={(event) => event.preventDefault()}
        onEnded={() => {
          setPlaying(false);
          setEnded(true);
        }}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
        onPause={() => setPlaying(false)}
        onPlay={() => {
          setPlaying(true);
          setControlsVisible(true);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        playsInline
        poster={posterUrl(release)}
        preload="none"
        src={streamUrl(release)}
      />
      <div className="media-center-controls" aria-label={`${release.title} playback controls`}>
        <button className="media-skip" onClick={() => skip(-10)} aria-label="Back 10 seconds">
          <Rewind size={22} />
          <span>10</span>
        </button>
        <button className="media-play-large" onClick={() => void togglePlay()} aria-label={ended ? "Replay video" : playing ? "Pause video" : "Play video"}>
          {ended ? <Rewind size={36} /> : playing ? <Pause size={34} fill="currentColor" /> : <Play size={38} fill="currentColor" />}
        </button>
        <button className="media-skip" onClick={() => skip(10)} aria-label="Forward 10 seconds">
          <FastForward size={22} />
          <span>10</span>
        </button>
      </div>
      {showNextUp && (
        <div className="media-next-up" aria-label="Next recommended MV">
          <span>{ended ? "Play next" : `Next up in ${remainingSeconds}s`}</span>
          <button onClick={onPlayNext} aria-label={`Play next MV ${nextRelease.artist} - ${nextRelease.title}`}>
            <img src={posterUrl(nextRelease)} alt="" loading="lazy" />
            <strong>{nextRelease.title}</strong>
            <em>{nextRelease.style}</em>
            <Play size={18} fill="currentColor" />
          </button>
        </div>
      )}
      <div className="media-bottom-bar">
        <label className="media-progress-label" aria-label="Video timeline">
          <input
            className="media-progress"
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={Math.min(currentTime, duration || currentTime)}
            onChange={(event) => seek(Number(event.currentTarget.value))}
            style={{ "--progress": `${progressPercent}%` } as CSSProperties}
          />
        </label>
        <div className="media-toolbar">
          <div className="media-left-controls">
            <button onClick={() => onSettingsChange((current) => ({ ...current, muted: !current.muted }))} aria-label={muted ? "Unmute video" : "Mute video"}>
              {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              className="media-volume"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={muted ? 0 : volume}
              onChange={(event) => changeVolume(Number(event.currentTarget.value))}
              aria-label="Video volume"
              aria-valuetext={`${volumePercent}%`}
            />
            <span className="media-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
          <span className="media-title">{release.artist} - {release.title}</span>
          <div className="media-action-controls">
            <button
              onClick={() => {
                revealControls();
                setSettingsOpen(false);
                setInfoOpen((current) => !current);
              }}
              aria-label="Show video info"
              className={infoOpen ? "active" : ""}
            >
              <Info size={18} />
            </button>
            <button onClick={() => {
              void handleCinemaModeButton();
            }} aria-label={cinemaButtonLabel} className={cinemaMode || isFullscreen ? "active" : ""}>
              <Film size={18} />
            </button>
            <button
              onClick={() => {
                revealControls();
                setInfoOpen(false);
                setSettingsOpen((current) => !current);
              }}
              aria-label="Open settings"
              className={settingsOpen ? "active" : ""}
            >
              <Settings2 size={18} />
            </button>
            <button onClick={() => void openFullscreen()} aria-label="Open fullscreen">
              <Maximize2 size={18} />
            </button>
          </div>
        </div>
        {settingsOpen && (
          <div className="media-popover media-settings-panel">
            <strong>Settings</strong>
            <div>
              <span>Speed</span>
              {[0.75, 1, 1.25, 1.5, 2].map((rate) => (
                <button key={rate} className={playbackRate === rate ? "active" : ""} onClick={() => {
                  revealControls();
                  onSettingsChange((current) => ({ ...current, playbackRate: rate }));
                }}>
                  {rate === 1 ? "Normal" : `${rate}x`}
                </button>
              ))}
            </div>
            <div>
              <span>Fit</span>
              <button className={fitMode === "contain" ? "active" : ""} onClick={() => {
                revealControls();
                onSettingsChange((current) => ({ ...current, fitMode: "contain" }));
              }}>Safe</button>
              <button className={fitMode === "cover" ? "active" : ""} onClick={() => {
                revealControls();
                onSettingsChange((current) => ({ ...current, fitMode: "cover" }));
              }}>Fill</button>
            </div>
            <p>Shortcuts: Space/K play, J/L skip, M mute, F fullscreen, T cinema, I info.</p>
          </div>
        )}
        {infoOpen && (
          <div className="media-popover media-info-panel">
            <strong>{release.artist} - {release.title}</strong>
            <span>{release.useCase} · {release.duration}</span>
            <p>{release.theme}</p>
            <p>{release.tagline}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0:00";
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function parseDuration(value: string) {
  const [minutes, seconds] = value.split(":").map(Number);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return 0;
  return minutes * 60 + seconds;
}

function posterUrl(release: ShowcaseRelease) {
  return `/showcase-posters/${release.slug}.jpg`;
}

function streamUrl(release: ShowcaseRelease) {
  return `/api/showcase/${release.slug}/stream`;
}

function SectionHeader({ eyebrow, title, sub }: { eyebrow: string; title: string; sub: string }) {
  return (
    <div className="section-header">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{sub}</p>
    </div>
  );
}

function TrackCard({ title, genre, duration, seed }: { title: string; genre: string; duration: string; seed: number }) {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="track-card">
      <div className={`cover-art cover-${(seed % 4) + 1}`}>
        <button className="play-float" onClick={() => setPlaying(!playing)}>{playing ? <Pause size={16} /> : <Play size={16} />}</button>
        <span>MV</span>
      </div>
      <div className="track-info">
        <h3>{title}</h3>
        <p>{genre} · {duration}</p>
        <Waveform seed={seed + 1} active={playing} />
      </div>
    </article>
  );
}

function TrustSection() {
  return (
    <section className="trust-section" id="trust">
      <div>
        <ShieldCheck size={22} />
        <h3>Built for original creation</h3>
        <p>Prompts that imitate artists, clone voices, or paste copyrighted lyrics are blocked before media jobs run.</p>
      </div>
      <div>
        <Lock size={22} />
        <h3>Provider-neutral outputs</h3>
        <p>Public credits stay clean while provider details remain inside private usage and cost logs.</p>
      </div>
      <div>
        <Gauge size={22} />
        <h3>Transparent credits</h3>
        <p>Every media job has a credit estimate. Failed provider jobs refund credits automatically.</p>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="cta-band">
      <span className="eyebrow">From idea to song + MV release</span>
      <h2>Turn one creative brief into a launch-ready music campaign.</h2>
      <div className="button-row center-row">
        <button className="primary" onClick={() => navigate("/app/studio")}>Open the Workstation <ArrowRight size={18} /></button>
        <button className="secondary" onClick={() => navigate("/contact?dept=brand&project=song_mv")}>Talk to Zolo <Mail size={17} /></button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <Logo />
      <span>AI Music Workstation · zolomusic.com</span>
      <button className="ghost" onClick={() => navigate("/contact")}>Contact</button>
    </footer>
  );
}

function PricingPage({ me }: { me: MeResponse | null }) {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "one_time">("monthly");
  const plans = [
    {
      id: "creator",
      name: "Creator",
      monthlyPrice: 29,
      oneTimePrice: 39,
      credits: "2,000 credits",
      runs: "~8 song + MV drafts",
      audience: "For YouTubers, indie artists, and solo creators launching regular content.",
      icon: Music2,
      features: ["MP3 song exports", "1080p lyric MV", "Cover art prompt", "YouTube/TikTok release copy", "Commercial use on paid plan"]
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 79,
      oneTimePrice: 119,
      credits: "7,500 credits",
      runs: "~30 song + MV jobs",
      audience: "Best for brands and artists who need repeatable campaigns with stronger control.",
      icon: Video,
      popular: true,
      features: ["WAV + stems workflow", "Cinematic MV templates", "Brand and artist kits", "Priority media queue", "License certificate per project"]
    },
    {
      id: "studio",
      name: "Studio",
      monthlyPrice: 299,
      oneTimePrice: 449,
      credits: "30,000 credits",
      runs: "High-volume campaign production",
      audience: "For labels, agencies, and teams building recurring song-led marketing systems.",
      icon: Gauge,
      features: ["Team workspace", "API-ready workflow", "Custom MV templates", "Batch release packs", "Dedicated production support"]
    }
  ];
  const packageFeatures = [
    ["Song + MV workflow", "Generate music and video assets in one release pipeline.", Wand2],
    ["Commercial clarity", "Paid plans include public credits and license certificates.", ShieldCheck],
    ["Release-ready exports", "Thumbnails, captions, metadata, and campaign copy included.", Download],
    ["Private provider layer", "Public outputs stay Zolo-branded and provider-neutral by default.", Lock]
  ];
  const monthlyDiscount = 25;

  return (
    <main>
      <Nav me={me} />
      <section className="page-hero compact pricing-hero">
        <span className="eyebrow">Pricing</span>
        <h1>Price the outcome: songs, MVs, and campaigns.</h1>
        <p>Like the NewFaceID flow, every customer can start with a preview-level path. Paid plans unlock finished songs, cinematic MVs, release packs, commercial usage, and higher-volume production credits.</p>
      </section>
      <section className="pricing-system">
        <div className="pricing-head">
          <div>
            <span className="eyebrow"><Sparkles size={14} /> Song + MV credits</span>
            <h2>Choose your production plan.</h2>
            <p>Monthly gives the best value for recurring releases. One-time packs are for a single campaign or trial production sprint.</p>
          </div>
          <div className="pricing-controls">
            <div className="billing-toggle" aria-label="Billing cycle">
              <button className={billingCycle === "monthly" ? "active" : ""} onClick={() => setBillingCycle("monthly")}>
                Monthly <strong>{monthlyDiscount}% off</strong>
              </button>
              <button className={billingCycle === "one_time" ? "active" : ""} onClick={() => setBillingCycle("one_time")}>
                One-time
              </button>
            </div>
            <span className="guarantee-badge"><ShieldCheck size={15} /> Preview first, upgrade for exports</span>
          </div>
        </div>

        <div className="pricing-cards">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const price = billingCycle === "monthly" ? plan.monthlyPrice : plan.oneTimePrice;
            const saved = Math.max(0, plan.oneTimePrice - plan.monthlyPrice);
            return (
              <article className={`pricing-card ${plan.popular ? "popular-plan" : ""}`} key={plan.id}>
                {plan.popular && <span className="popular">Most popular</span>}
                <div className="plan-top">
                  <div>
                    <h2>{plan.name}</h2>
                    <p>{plan.audience}</p>
                  </div>
                  <span className="plan-icon"><Icon size={22} /></span>
                </div>
                <div className="plan-billing">
                  {billingCycle === "monthly" && <span className="saving-pill">Save ${saved}/mo vs one-time</span>}
                  <div className="price">${price}<em>{billingCycle === "monthly" ? "/mo" : " one-time"}</em></div>
                  <strong>{plan.credits}</strong>
                  <span>{plan.runs}</span>
                </div>
                <button
                  className={plan.popular ? "primary" : "secondary"}
                  onClick={() => navigate(`/contact?plan=${plan.id}&dept=${plan.id === "creator" ? "creator" : "brand"}&project=song_mv`)}
                >
                  Choose {plan.name} <ArrowRight size={16} />
                </button>
                <div className="plan-included">
                  <h3>What's included</h3>
                  <ul>
                    {plan.features.map((feature) => (
                      <li key={feature}><Check size={16} /> {feature}</li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
        <div className="pricing-trust-row">
          <span><ShieldCheck size={18} /> Commercial-use wording by plan</span>
          <span><Headphones size={18} /> Creator support for release setup</span>
          <span><Sparkles size={18} /> Built for song + MV output</span>
        </div>
      </section>
      <section className="pricing-includes section">
        <SectionHeader
          eyebrow="All packages include"
          title="Not just music generation. A release system."
          sub="Zolo's paid value is the combined song, MV, thumbnail, caption, metadata, and rights workflow."
        />
        <div className="feature-grid">
          {packageFeatures.map(([title, body, Icon]) => (
            <div className="feature-card" key={title as string}>
              <Icon size={22} />
              <h3>{title as string}</h3>
              <p>{body as string}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="credit-guide">
        <div>
          <h2>Need a full campaign or label workflow?</h2>
          <p>Studio packages can bundle artist kits, custom MV templates, priority jobs, campaign metadata, and team review flow for recurring releases.</p>
          <button className="primary" onClick={() => navigate("/contact?dept=enterprise&project=api_studio&plan=studio")}>Contact sales <ArrowRight size={16} /></button>
        </div>
        <div className="cost-table">
          {[
            ["Song generation", "25+ credits"],
            ["Cinematic MV", "60+ credits"],
            ["Stem extraction", "20+ credits"],
            ["Release pack", "15+ credits"]
          ].map(([k, v]) => (
            <div key={k}><span>{k}</span><strong>{v}</strong></div>
          ))}
        </div>
      </section>
      <section className="section business-model">
        <SectionHeader
          eyebrow="Unicorn SaaS wedge"
          title="A creative OS for music-led brands."
          sub="The defensible business is workflow ownership: project history, artist kits, approved voices, MV templates, credits, rights certificates, and team production memory."
        />
        <div className="feature-grid">
          {[
            ["Creators", "Monthly credits for songs, MVs, thumbnails, captions, and launch packs.", Headphones],
            ["Brands", "Campaign jingle + cinematic MV packages with repeatable brand kits.", Video],
            ["Studios", "Priority queue, API volume, custom templates, and multi-seat production controls.", Gauge]
          ].map(([title, body, Icon]) => (
            <div className="feature-card" key={title as string}>
              <Icon size={22} />
              <h3>{title as string}</h3>
              <p>{body as string}</p>
            </div>
          ))}
        </div>
      </section>
      <TrustSection />
      <Footer />
    </main>
  );
}

type ContactStatus = "idle" | "sending" | "sent" | "error";

function ContactPage({ me }: { me: MeResponse | null }) {
  const initial = useMemo(() => initialContactState(), []);
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState<ContactStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      await api("/api/contact", {
        method: "POST",
        body: JSON.stringify(form)
      });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Message failed");
    }
  }

  return (
    <main>
      <Nav me={me} />
      <section className="page-hero compact contact-hero">
        <span className="eyebrow"><Mail size={15} /> Contact Zolo Music</span>
        <h1>Plan a song + MV campaign.</h1>
        <p>Tell us whether you are a creator, brand, agency, label, or studio team. The form is pre-filled for the most common high-value Zolo workflows.</p>
      </section>
      <section className="contact-layout">
        <form className="contact-form" onSubmit={submit}>
          <div className="two-col">
            <label>
              Name
              <input value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Your name" required />
            </label>
            <label>
              Email
              <input value={form.email} onChange={(event) => update("email", event.target.value)} placeholder="you@company.com" type="email" required />
            </label>
          </div>
          <label>
            Company / artist name
            <input value={form.company} onChange={(event) => update("company", event.target.value)} placeholder="Brand, agency, label, or artist" />
          </label>
          <div className="two-col">
            <ContactSelect
              label="Department"
              value={form.department}
              onChange={(value) => update("department", value)}
              options={[
                ["brand", "Brand / Marketing"],
                ["creator", "Creator / Artist"],
                ["agency", "Agency"],
                ["label", "Label / Music Company"],
                ["enterprise", "Enterprise / API"],
                ["support", "Support"]
              ]}
            />
            <ContactSelect
              label="Project"
              value={form.projectType}
              onChange={(value) => update("projectType", value)}
              options={[
                ["song_mv", "Song + MV generation"],
                ["brand_jingle", "Brand jingle + campaign MV"],
                ["artist_launch", "Artist launch package"],
                ["social_campaign", "Short-form social campaign"],
                ["api_studio", "Studio/API workflow"],
                ["other", "Other"]
              ]}
            />
            <ContactSelect
              label="Plan interest"
              value={form.planInterest}
              onChange={(value) => update("planInterest", value)}
              options={[
                ["pro", "Pro"],
                ["studio", "Studio"],
                ["creator", "Creator"],
                ["enterprise", "Enterprise"],
                ["not_sure", "Not sure yet"]
              ]}
            />
            <ContactSelect
              label="Reference work"
              value={form.selectedWork}
              onChange={(value) => update("selectedWork", value)}
              options={[
                ["", "No specific reference"],
                ...showcaseReleases.map((release) => [release.slug, `${release.artist} - ${release.title}`] as [string, string])
              ]}
            />
          </div>
          <div className="two-col">
            <ContactSelect
              label="Budget range"
              value={form.budget}
              onChange={(value) => update("budget", value)}
              options={[
                ["", "Not decided"],
                ["under_500", "Under $500"],
                ["500_2000", "$500 - $2,000"],
                ["2000_10000", "$2,000 - $10,000"],
                ["10000_plus", "$10,000+"]
              ]}
            />
            <ContactSelect
              label="Timeline"
              value={form.timeline}
              onChange={(value) => update("timeline", value)}
              options={[
                ["", "Flexible"],
                ["this_week", "This week"],
                ["two_weeks", "Within 2 weeks"],
                ["this_month", "This month"],
                ["ongoing", "Ongoing production"]
              ]}
            />
          </div>
          <label>
            Brief
            <textarea value={form.message} onChange={(event) => update("message", event.target.value)} required />
          </label>
          <input className="hidden-field" value={form.website} onChange={(event) => update("website", event.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" />
          {status === "sent" && <div className="form-success">Message sent. Philip will receive the enquiry at admin email.</div>}
          {status === "error" && <div className="form-error">{error}</div>}
          <button className="primary contact-submit" disabled={status === "sending"}>
            {status === "sending" ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
            Send enquiry
          </button>
        </form>
        <aside className="contact-side">
          <span className="eyebrow">High-value use cases</span>
          <h2>What Zolo can sell.</h2>
          <div className="contact-usecases">
            {[
              ["Brand campaign", "Original hook, full MV, vertical cuts, thumbnails, captions."],
              ["Artist launch", "Debut identity, song versions, cinematic MV, release metadata."],
              ["Studio workflow", "Repeatable project memory, artist kits, stems, API queue, licensing trail."]
            ].map(([title, body]) => (
              <div key={title}>
                <strong>{title}</strong>
                <span>{body}</span>
              </div>
            ))}
          </div>
        </aside>
      </section>
      <Footer />
    </main>
  );
}

function initialContactState() {
  const params = new URLSearchParams(window.location.search);
  const work = params.get("work") || "";
  const plan = params.get("plan") || "pro";
  const department = params.get("dept") || "brand";
  const projectType = params.get("project") || "song_mv";
  const selectedRelease = showcaseReleases.find((release) => release.slug === work) || showcaseReleases[0];
  const selectedWork = showcaseReleases.some((release) => release.slug === work) ? work : "";
  return {
    name: "",
    email: "",
    company: "",
    department: ["creator", "brand", "agency", "label", "enterprise", "support"].includes(department) ? department : "brand",
    projectType: ["song_mv", "brand_jingle", "artist_launch", "social_campaign", "api_studio", "other"].includes(projectType) ? projectType : "song_mv",
    planInterest: ["creator", "pro", "studio", "enterprise", "not_sure"].includes(plan) ? plan : "pro",
    budget: "",
    timeline: "",
    selectedWork,
    message: `Hi Zolo Music,\n\nI am interested in creating a song + music video package similar to ${selectedRelease.artist} - ${selectedRelease.title}.\n\nWe need help with music direction, MV visuals, thumbnail/media player assets, and a release-ready campaign pack.\n\nPlease send me the recommended package, timeline, and pricing.`,
    website: ""
  };
}

function ContactSelect({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="select-label">
      {label}
      <span>
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>{optionLabel}</option>
          ))}
        </select>
        <ChevronDown size={16} />
      </span>
    </label>
  );
}

function AppFrame({ me, active, children }: { me: MeResponse | null; active: Route; children: React.ReactNode }) {
  return (
    <main>
      <Nav me={me} app />
      <div className="workspace">
        <aside className="sidebar">
          {[
            ["create", "Create", Wand2, "/app/create"],
            ["studio", "Studio", Settings2, "/app/studio"],
            ["result", "Result", Music2, "/app/result"],
            ["brand", "Brand Kit", ShieldCheck, "/app/brand"],
            ["library", "Library", Library, "/app/library"],
            ["pricing", "Pricing", Zap, "/pricing"]
          ].map(([id, label, Icon, path]) => (
            <button key={id as string} className={active === id ? "active" : ""} onClick={() => navigate(path as string)}>
              <Icon size={17} /> {label as string}
            </button>
          ))}
          <div className="upgrade-note">
            <Sparkles size={16} />
            <strong>Pro unlocks stems</strong>
            <span>WAV, priority queue, brand kits, and release packs.</span>
          </div>
        </aside>
        {children}
      </div>
    </main>
  );
}

function AuthPanel({ setMe }: { setMe: (me: MeResponse | null) => void }) {
  const [mode, setMode] = useState<"login" | "register">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api(mode === "register" ? "/api/auth/register" : "/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, name: name || undefined })
      });
      const next = await api<NullableMeResponse>("/api/auth/me");
      setMe(next.user ? next : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Auth failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="auth-panel">
      <span className="eyebrow"><Lock size={14} /> Sign in required</span>
      <h2>Create your first Zolo Music project.</h2>
      <p>Register or sign in to use real TEXT_CHAIN generation and credit tracking.</p>
      <form onSubmit={submit}>
        {mode === "register" && <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" autoComplete="name" />}
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" required autoComplete="email" />
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          required
          minLength={8}
          autoComplete={mode === "register" ? "new-password" : "current-password"}
        />
        {error && <div className="form-error">{error}</div>}
        <button className="primary" disabled={loading}>
          {loading ? <Loader2 className="spin" size={17} /> : null}
          {mode === "register" ? "Create account" : "Sign in"}
        </button>
      </form>
      <div className="auth-actions">
        <button className="ghost" onClick={() => setMode(mode === "register" ? "login" : "register")}>
          {mode === "register" ? "Use existing account" : "Create new account"}
        </button>
        <a href="/auth/google/start" className="secondary">Continue with Google</a>
      </div>
    </section>
  );
}

function CreatePage({ me, setMe }: { me: MeResponse | null; setMe: (me: MeResponse | null) => void }) {
  const [prompt, setPrompt] = useState("VÉLA by Zolo debut single TAP TAP: bright AI pop for shopping energy, clean chorus, premium global girl group mood.");
  const [lyricsMode, setLyricsMode] = useState<"auto" | "own" | "instrumental">("auto");
  const [genre, setGenre] = useState("Synth Pop");
  const [mood, setMood] = useState("Uplifting");
  const [language, setLanguage] = useState("English");
  const [voice, setVoice] = useState("Female · Bright");
  const [durationSeconds, setDurationSeconds] = useState(150);
  const [energy, setEnergy] = useState(72);
  const [creativity, setCreativity] = useState(68);
  const [versions, setVersions] = useState(2);
  const [phase, setPhase] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const creditCost = useMemo(() => Math.max(4, versions * (Math.ceil(durationSeconds / 60) + 2)), [durationSeconds, versions]);

  async function generate() {
    setPhase("loading");
    setError(null);
    try {
      const payload = await api<any>("/api/generations", {
        method: "POST",
        body: JSON.stringify({
          prompt,
          lyricsMode,
          genre,
          mood,
          language,
          voice,
          durationSeconds,
          energy,
          creativity,
          versions,
          outputFormat: "mp3",
          mvAspectRatio: "16:9",
          commercialUse: true
        })
      });
      setResult(payload);
      setPhase("done");
      api<NullableMeResponse>("/api/auth/me")
        .then((payload) => setMe(payload.user ? payload : null))
        .catch(() => undefined);
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Generation failed");
    }
  }

  return (
    <AppFrame me={me} active="create">
      <section className="app-content">
        <div className="app-heading">
          <div>
            <h1>Create</h1>
            <p>Draft the concept, lyrics direction, voice, and media job settings before entering Studio.</p>
          </div>
          <CreditBadge credits={me?.credits.remaining_credits ?? 0} />
        </div>
        {!me ? (
          <AuthPanel setMe={setMe} />
        ) : (
          <div className="create-grid">
            <div className="control-panel">
              <label>
                Prompt
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} />
              </label>
              <Segmented
                value={lyricsMode}
                onChange={(v) => setLyricsMode(v as typeof lyricsMode)}
                options={[
                  ["auto", "Auto-write lyrics"],
                  ["own", "Use my lyrics"],
                  ["instrumental", "Instrumental"]
                ]}
              />
              <div className="two-col">
                <Select label="Genre" value={genre} onChange={setGenre} options={genres} />
                <Select label="Mood" value={mood} onChange={setMood} options={moods} />
                <Select label="Language" value={language} onChange={setLanguage} options={languages} />
                <Select label="Voice" value={voice} onChange={setVoice} options={voices} />
              </div>
              <Range label="Duration" value={durationSeconds} min={30} max={300} step={15} suffix="s" onChange={setDurationSeconds} />
              <Range label="Energy" value={energy} min={0} max={100} onChange={setEnergy} />
              <Range label="Creativity" value={creativity} min={0} max={100} onChange={setCreativity} />
              <Range label="Versions" value={versions} min={1} max={4} onChange={setVersions} />
              <button className="primary generate-btn" onClick={generate} disabled={phase === "loading"}>
                {phase === "loading" ? <Loader2 className="spin" size={18} /> : <Sparkles size={18} />}
                {phase === "loading" ? "Planning..." : "Generate text plan"} <span>{creditCost} credits</span>
              </button>
              {error && <div className="form-error">{error}</div>}
            </div>
            <GenerationOutput phase={phase} result={result} />
          </div>
        )}
      </section>
    </AppFrame>
  );
}

function GenerationOutput({ phase, result }: { phase: string; result: any }) {
  if (phase === "loading") {
    return (
      <div className="output-panel center">
        <Loader2 className="spin glow-icon" size={38} />
        <h2>Writing lyrics, structure, and media-ready metadata</h2>
        <p>TEXT_CHAIN prepares the project before queued audio and MV jobs run in Studio.</p>
      </div>
    );
  }
  if (phase === "done" && result) {
    return (
      <div className="output-panel">
        <div className="output-top">
          <span className="pill">Text plan ready</span>
          <button className="secondary" onClick={() => navigate("/app/studio")}>Open Studio</button>
        </div>
        <h2>{result.summary}</h2>
        <p>{result.enhancedPrompt}</p>
        <div className="result-list">
          {result.versions.map((version: any, index: number) => (
            <TrackCard key={version.title} title={version.title} genre={version.genre} duration={`${Math.round(version.durationSeconds / 60)}:${String(version.durationSeconds % 60).padStart(2, "0")}`} seed={index} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="output-panel center">
      <Headphones size={42} />
      <h2>Your workstation brief appears here.</h2>
      <p>Tweak the prompt, then generate multiple lyrics and metadata directions before running media jobs.</p>
    </div>
  );
}

function ResultPage({ me }: { me: MeResponse | null }) {
  return (
    <AppFrame me={me} active="result">
      <section className="app-content">
        <div className="result-layout">
          <div className="big-cover cover-art cover-1"><span>VÉLA by Zolo · TAP TAP</span></div>
          <div className="result-detail">
            <span className="pill">Flagship release package</span>
            <h1>TAP TAP</h1>
            <p>VÉLA by Zolo · AI Pop · English · 2:48</p>
            <MiniPlayer />
            <div className="button-row">
              <button className="primary"><Download size={16} /> MP3</button>
              <button className="secondary"><Download size={16} /> WAV</button>
              <button className="secondary" onClick={() => navigate("/app/studio")}><Video size={16} /> Open Studio</button>
            </div>
            <div className="lyrics-box">
              <h3>Lyrics</h3>
              <p>[Verse] Grab your cart, it's time to shop. Click those deals, keep the good times pop.</p>
              <p>[Chorus] Tap tap, feel the glow. Every move is ready to go.</p>
            </div>
            <div className="responsible-note">Written, Composed & Produced by Pak · A Zolo Music Original Production</div>
          </div>
        </div>
      </section>
    </AppFrame>
  );
}

function StudioPage({ me }: { me: MeResponse | null }) {
  const [aspect, setAspect] = useState("9:16");
  const [energy, setEnergy] = useState(72);
  const jobs = [
    ["Song generation", "completed", "24 credits"],
    ["Stem extraction", "queued", "16 credits"],
    ["Lyric MV export", "ready", "29 credits"],
    ["Replace section 0:57", "review", "11 credits"]
  ];
  const sections = [
    ["Intro", "0:00", "0:10"],
    ["Verse", "0:10", "0:32"],
    ["Chorus", "0:32", "0:50"],
    ["Edit zone", "0:57", "1:12"],
    ["Final hook", "1:48", "2:28"]
  ];
  return (
    <AppFrame me={me} active="studio">
      <section className="app-content">
        <div className="app-heading">
          <div>
            <h1>Studio</h1>
            <p>Queue media jobs, edit sections, inspect stems, and package the release.</p>
          </div>
          <button className="primary"><Sparkles size={16} /> Run music job</button>
        </div>
        <div className="studio-grid">
          <div className="studio-main">
            <div className="studio-hero-card">
              <div>
                <span className="eyebrow">Current project</span>
                <h2>VÉLA by Zolo · TAP TAP</h2>
                <p>Written, Composed & Produced by Pak · A Zolo Music Original Production</p>
              </div>
              <MiniPlayer />
            </div>
            <div className="timeline-panel">
              <div className="panel-title">
                <h3>Lyrics timeline</h3>
                <span>2:48 · 5 sections</span>
              </div>
              <div className="section-timeline">
                {sections.map(([name, start, end], index) => (
                  <button key={name} className={name === "Edit zone" ? "attention" : ""}>
                    <strong>{name}</strong>
                    <span>{start} - {end}</span>
                    <i style={{ width: `${72 + index * 10}px` }} />
                  </button>
                ))}
              </div>
            </div>
            <div className="asset-table">
              <div className="panel-title">
                <h3>Release assets</h3>
                <span>Provider-neutral public output</span>
              </div>
              {[
                ["Main mix", "MP3", "Ready", "audio/user/project/main.mp3"],
                ["Master", "WAV", "Pro", "audio/user/project/master.wav"],
                ["Vocals stem", "WAV", "Queued", "stems/project/vocals.wav"],
                ["License certificate", "TXT", "Ready", "certificates/project/license.txt"]
              ].map(([name, format, status, path]) => (
                <div key={name}>
                  <span>{name}</span>
                  <strong>{format}</strong>
                  <em>{status}</em>
                  <code>{path}</code>
                </div>
              ))}
            </div>
          </div>
          <div className="control-panel">
            <Select label="Job type" value="Vocal song" onChange={() => undefined} options={["Vocal song", "Instrumental", "Remix", "Extend", "Replace section", "Extract stems", "Lyric MV"]} />
            <Select label="Provider route" value="Auto · commercial primary" onChange={() => undefined} options={["Auto · commercial primary", "Mock preview", "Stable fallback", "Background/SFX route"]} />
            <Select label="MV template" value="Cinematic Lyrics" onChange={() => undefined} options={["Cinematic Lyrics", "Kinetic Type", "Minimal Studio", "Neon Stage"]} />
            <Segmented value={aspect} onChange={setAspect} options={[["16:9", "16:9"], ["9:16", "9:16"], ["1:1", "1:1"]]} />
            <Range label="Visual energy" value={energy} min={0} max={100} onChange={setEnergy} />
            <div className="job-list">
              {jobs.map(([name, status, credits]) => (
                <div key={name}>
                  <span>{name}</span>
                  <strong>{status}</strong>
                  <em>{credits}</em>
                </div>
              ))}
            </div>
            <button className="secondary"><Download size={16} /> Export release pack</button>
          </div>
          <div className={`mv-preview aspect-${aspect.replace(":", "-")}`}>
            <div className="cover-art cover-2" />
            <div className="caption">TAP TAP</div>
            <Waveform seed={8} active />
          </div>
        </div>
      </section>
    </AppFrame>
  );
}

function BrandKitPage({ me }: { me: MeResponse | null }) {
  return (
    <AppFrame me={me} active="brand">
      <section className="app-content">
        <div className="app-heading">
          <div>
            <h1>Brand Kit</h1>
            <p>Control public artist identity, credits, title cards, and release metadata.</p>
          </div>
          <button className="primary"><ShieldCheck size={16} /> Save kit</button>
        </div>
        <div className="brand-grid">
          <div className="control-panel">
            <label>
              Artist name
              <input value="VÉLA by Zolo" readOnly />
            </label>
            <label>
              Public credit
              <input value="Written, Composed & Produced by Pak" readOnly />
            </label>
            <label>
              Production line
              <input value="A Zolo Music Original Production" readOnly />
            </label>
            <Select label="Visual style" value="Premium digital commerce pop" onChange={() => undefined} options={["Premium digital commerce pop", "Minimal studio", "Neon performance", "Luxury cinematic"]} />
            <Select label="Voice direction" value="Female · Bright · Global pop" onChange={() => undefined} options={["Female · Bright · Global pop", "Female · Warm R&B", "Duet · Pop", "Instrumental only"]} />
          </div>
          <div className="release-panel">
            <span className="eyebrow">Title card preview</span>
            <h2>ZOLO MUSIC PRESENTS</h2>
            <div className="release-title">VÉLA</div>
            <p>by Zolo</p>
            <strong>TAP TAP</strong>
            <small>Written, Composed & Produced by Pak</small>
            <small>A Zolo Music Original Production</small>
          </div>
        </div>
      </section>
    </AppFrame>
  );
}

function LibraryPage({ me }: { me: MeResponse | null }) {
  const assets = [
    ["TAP TAP", "Project", "Release pack ready"],
    ["TAP TAP main mix", "MP3", "Commercial plan"],
    ["TAP TAP stems", "WAV stems", "Queued"],
    ["VÉLA title cards", "MV", "9:16 + 16:9"],
    ["License certificate", "Rights", "Provider-neutral"],
    ["Brand Spark", "Jingle", "Draft"]
  ];
  return (
    <AppFrame me={me} active="library">
      <section className="app-content">
        <div className="app-heading">
          <div>
            <h1>Library</h1>
            <p>Projects, jobs, audio assets, stems, MV exports, and license certificates.</p>
          </div>
          <div className="search"><Search size={16} /> Search projects</div>
        </div>
        <div className="library-grid">
          {[...assets, ...libraryExamples.slice(1, 4)].map((item, index) => (
            <TrackCard key={item[0]} title={item[0]} genre={`${item[1]} · ${item[2]}`} duration={index % 2 ? "Asset" : "Job"} seed={index} />
          ))}
        </div>
      </section>
    </AppFrame>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="select-label">
      {label}
      <span>
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((option) => <option key={option}>{option}</option>)}
        </select>
        <ChevronDown size={16} />
      </span>
    </label>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  return (
    <div className="segmented">
      {options.map(([id, label]) => (
        <button key={id} className={value === id ? "active" : ""} onClick={() => onChange(id)}>{label}</button>
      ))}
    </div>
  );
}

function Range({ label, value, min, max, step = 1, suffix = "", onChange }: { label: string; value: number; min: number; max: number; step?: number; suffix?: string; onChange: (v: number) => void }) {
  return (
    <label className="range-label">
      <span>{label}<em>{value}{suffix}</em></span>
      <input type="range" value={value} min={min} max={max} step={step} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

function Waveform({ seed, active }: { seed: number; active?: boolean }) {
  const bars = Array.from({ length: 38 }, (_, index) => {
    const h = 22 + Math.round((Math.sin(index * 0.7 + seed) * 0.5 + 0.5) * 44);
    return h;
  });
  return (
    <div className={`waveform ${active ? "active" : ""}`}>
      {bars.map((height, index) => <i key={index} style={{ height }} />)}
    </div>
  );
}

function MiniPlayer({ title = "TAP TAP", meta = "VÉLA by Zolo · 2:11", seed = 4, autoActive = false }: { title?: string; meta?: string; seed?: number; autoActive?: boolean }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="mini-player">
      <button className="play" onClick={() => setPlaying(!playing)} aria-label={playing ? "Pause studio preview" : "Play studio preview"}>{playing ? <Pause size={17} /> : <Play size={17} />}</button>
      <div>
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <Waveform seed={seed} active={playing || autoActive} />
      <button className="mini-settings" onClick={() => navigate("/app/studio")} aria-label="Open studio settings">
        <Settings2 size={17} />
      </button>
    </div>
  );
}
